// Regular expressions for validation
const REGEX = {
  // Matches any non-whitespace character, 1-15 characters long
  NAME: /^[\S]{1,15}$/,

  // Matches any non-whitespace character, 1-35 characters long
  KANA: /^[\S]{1,35}$/,

  // Matches Japanese phone format 0XXXXXXXXXX (11 digits total)
  PHONE: /^0\d{10}$/,

  // Postal code format (XXX-XXXX)
  POSTAL: /^\d{3}-\d{4}$/,

  // Any non-whitespace character, 1-16 characters long
  CITY: /^[\S]{1,16}$/,

  // Any non-whitespace character, 1-16 characters long
  STREET: /^[\S]{1,16}$/,

  // Any non-whitespace character, 1-16 characters long
  BUILDING: /^[\S]{1,16}$/
};

const MAX_LENGTHS = {
  NAME: 15,
  KANA: 35,
  PHONE: 11, // Total digits for format 0XXXXXXXXXX
  POSTAL: 8, // Including hyphen
  CITY: 16,
  STREET: 16,
  BUILDING: 16
};

/**
 * Validates a name field (Chinese characters, hiragana, katakana, English letters)
 * @param {string} value - The name to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateName = (value) => {
  if (!value) return { isValid: false, error: 'validation.address.name_required' };
  if (value.length > MAX_LENGTHS.NAME) {
    return { isValid: false, error: 'validation.address.name_max_length' };
  }
  if (!REGEX.NAME.test(value)) {
    return { isValid: false, error: 'validation.address.name_invalid_characters' };
  }
  return { isValid: true };
};

/**
 * Validates a kana name field (katakana only)
 * @param {string} value - The kana name to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateKana = (value) => {
  if (!value) return { isValid: false, error: 'validation.address.kana_required' };
  if (value.length > MAX_LENGTHS.KANA) {
    return { isValid: false, error: 'validation.address.kana_max_length' };
  }
  if (!REGEX.KANA.test(value)) {
    return { isValid: false, error: 'validation.address.kana_invalid_characters' };
  }
  return { isValid: true };
};

/**
 * Validates a phone number (half-width numbers and hyphens)
 * @param {string} value - The phone number to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validatePhone = (value) => {
  if (!value) return { isValid: false, error: 'validation.address.phone_required' };
  const digitsOnly = value.replace(/-/g, '');
  if (digitsOnly.length !== MAX_LENGTHS.PHONE) {
    return { isValid: false, error: 'validation.address.phone_invalid_length' };
  }
  if (!REGEX.PHONE.test(digitsOnly)) {
    return { isValid: false, error: 'validation.address.phone_invalid_format' };
  }
  return { isValid: true };
};

/**
 * Validates a postal code (XXX-XXXX format)
 * @param {string} value - The postal code to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validatePostal = (value) => {
  if (!value) return { isValid: false, error: 'validation.address.postal_code_required' };
  if (!REGEX.POSTAL.test(value)) {
    return { isValid: false, error: 'validation.address.postal_code_invalid_format' };
  }
  return { isValid: true };
};

/**
 * Validates a city name (Chinese characters, hiragana, katakana)
 * @param {string} value - The city name to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateCity = (value) => {
  if (!value) return { isValid: false, error: 'validation.address.city_required' };
  if (value.length > MAX_LENGTHS.CITY) {
    return { isValid: false, error: 'validation.address.city_max_length' };
  }
  if (!REGEX.CITY.test(value)) {
    return { isValid: false, error: 'validation.address.city_invalid_characters' };
  }
  return { isValid: true };
};

/**
 * Validates a street address (Chinese characters and half-width numbers)
 * @param {string} value - The street address to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateStreet = (value) => {
  if (!value) return { isValid: false, error: 'validation.address.street_required' };
  if (value.length > MAX_LENGTHS.STREET) {
    return { isValid: false, error: 'validation.address.street_max_length' };
  }
  if (!REGEX.STREET.test(value)) {
    return { isValid: false, error: 'validation.address.street_invalid_characters' };
  }
  return { isValid: true };
};

/**
 * Validates a building name (Chinese characters, kana, English letters, numbers)
 * @param {string} value - The building name to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateBuilding = (value) => {
  if (!value) return { isValid: true }; // Building is optional
  if (value.length > MAX_LENGTHS.BUILDING) {
    return { isValid: false, error: 'validation.address.building_max_length' };
  }
  if (!REGEX.BUILDING.test(value)) {
    return { isValid: false, error: 'validation.address.building_invalid_characters' };
  }
  return { isValid: true };
};

/**
 * Validates the entire address form data
 * @param {Object} formData - The form data to validate
 * @returns {{ isValid: boolean, errors: Object.<string, string> }}
 */
export const validateAddressForm = (formData) => {
  const errors = {};
  let isValid = true;

  // Validate name fields
  const lastNameResult = validateName(formData.lastName);
  if (!lastNameResult.isValid) {
    errors.lastName = lastNameResult.error;
    isValid = false;
  }

  const firstNameResult = validateName(formData.firstName);
  if (!firstNameResult.isValid) {
    errors.firstName = firstNameResult.error;
    isValid = false;
  }

  // Validate kana name fields
  const lastNameKanaResult = validateKana(formData.lastNameKana);
  if (!lastNameKanaResult.isValid) {
    errors.lastNameKana = lastNameKanaResult.error;
    isValid = false;
  }

  const firstNameKanaResult = validateKana(formData.firstNameKana);
  if (!firstNameKanaResult.isValid) {
    errors.firstNameKana = firstNameKanaResult.error;
    isValid = false;
  }

  // Validate contact information
  const phoneResult = validatePhone(formData.phone);
  if (!phoneResult.isValid) {
    errors.phone = phoneResult.error;
    isValid = false;
  }

  const postalResult = validatePostal(formData.postalCode);
  if (!postalResult.isValid) {
    errors.postalCode = postalResult.error;
    isValid = false;
  }

  // Validate address fields
  if (!formData.prefecture) {
    errors.prefecture = 'validation.address.prefecture_required';
    isValid = false;
  }

  const cityResult = validateCity(formData.city);
  if (!cityResult.isValid) {
    errors.city = cityResult.error;
    isValid = false;
  }

  const streetResult = validateStreet(formData.street);
  if (!streetResult.isValid) {
    errors.street = streetResult.error;
    isValid = false;
  }

  const buildingResult = validateBuilding(formData.building);
  if (!buildingResult.isValid) {
    errors.building = buildingResult.error;
    isValid = false;
  }

  return { isValid, errors };
}; 